/**
 * Support Service API Contracts
 * Request/Response schemas for the support service
 */

import { z } from 'zod';

// ============================================================================
// Support Ticket Priority and Status
// ============================================================================

export const TicketPrioritySchema = z.enum(['LOW', 'MEDIUM', 'HIGH', 'URGENT']);
export type TicketPriority = z.infer<typeof TicketPrioritySchema>;

export const SupportTicketStatusSchema = z.enum([
  'OPEN',
  'IN_PROGRESS',
  'WAITING_CUSTOMER',
  'RESOLVED',
  'CLOSED',
]);
export type SupportTicketStatus = z.infer<typeof SupportTicketStatusSchema>;

// ============================================================================
// Support Tickets
// ============================================================================

export const SupportTicketSchema = z.object({
  id: z.string(),
  ticketNumber: z.string(),
  customerId: z.string(),
  customerEmail: z.string().email(),
  customerName: z.string(),
  category: z.string(),
  subject: z.string(),
  description: z.string(),
  priority: TicketPrioritySchema,
  status: SupportTicketStatusSchema,
  assignedTo: z.string().nullable(),
  relatedOrderId: z.string().nullable(),
  relatedTicketId: z.string().nullable(),
  resolvedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type SupportTicket = z.infer<typeof SupportTicketSchema>;

export const CreateTicketRequestSchema = z.object({
  customerId: z.string().min(1),
  customerEmail: z.string().email(),
  customerName: z.string().min(1).max(100),
  category: z.string().min(1).max(50),
  subject: z.string().min(1).max(200),
  description: z.string().min(1).max(5000),
  priority: TicketPrioritySchema.optional().default('MEDIUM'),
  relatedOrderId: z.string().uuid().optional(),
  relatedTicketId: z.string().uuid().optional(),
});

export type CreateTicketRequest = z.infer<typeof CreateTicketRequestSchema>;

export const UpdateTicketRequestSchema = z.object({
  status: SupportTicketStatusSchema.optional(),
  priority: TicketPrioritySchema.optional(),
  assignedTo: z.string().uuid().nullable().optional(),
});

export type UpdateTicketRequest = z.infer<typeof UpdateTicketRequestSchema>;

export const GetTicketsByStatusQuerySchema = z.object({
  status: SupportTicketStatusSchema,
  limit: z.coerce.number().int().positive().max(100).default(50),
});

export type GetTicketsByStatusQuery = z.infer<typeof GetTicketsByStatusQuerySchema>;

// ============================================================================
// Ticket Messages
// ============================================================================

export const SenderTypeSchema = z.enum(['CUSTOMER', 'STAFF', 'SYSTEM']);
export type SenderType = z.infer<typeof SenderTypeSchema>;

export const TicketMessageSchema = z.object({
  id: z.string(),
  ticketId: z.string(),
  senderId: z.string(),
  senderType: SenderTypeSchema,
  senderName: z.string(),
  content: z.string(),
  isInternal: z.boolean(),
  createdAt: z.string().datetime(),
});

export type TicketMessage = z.infer<typeof TicketMessageSchema>;

export const AddMessageRequestSchema = z.object({
  senderId: z.string().uuid(),
  senderType: SenderTypeSchema,
  senderName: z.string().min(1).max(100),
  content: z.string().min(1).max(10000),
  isInternal: z.boolean().optional().default(false),
});

export type AddMessageRequest = z.infer<typeof AddMessageRequestSchema>;

// ============================================================================
// Ticket Statistics
// ============================================================================

export const TicketStatisticsSchema = z.object({
  open: z.number().int().nonnegative(),
  inProgress: z.number().int().nonnegative(),
  waitingCustomer: z.number().int().nonnegative(),
  resolvedToday: z.number().int().nonnegative(),
  createdToday: z.number().int().nonnegative(),
});

export type TicketStatistics = z.infer<typeof TicketStatisticsSchema>;

// ============================================================================
// FAQ
// ============================================================================

export const FaqItemSchema = z.object({
  id: z.string(),
  category: z.string(),
  question: z.string(),
  answer: z.string(),
  keywords: z.array(z.string()),
  sortOrder: z.number().int(),
  isPublished: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type FaqItem = z.infer<typeof FaqItemSchema>;

export const GetFaqQuerySchema = z.object({
  category: z.string().optional(),
  search: z.string().optional(),
});

export type GetFaqQuery = z.infer<typeof GetFaqQuerySchema>;

// ============================================================================
// Canned Responses
// ============================================================================

export const CannedResponseSchema = z.object({
  id: z.string(),
  title: z.string(),
  content: z.string(),
  category: z.string().nullable(),
  shortcut: z.string().nullable(),
  isActive: z.boolean(),
  createdBy: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export * from './admin';

export type CannedResponse = z.infer<typeof CannedResponseSchema>;
